import React from 'react';
import { Box, Typography, Container, Paper } from '@mui/material';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';

const TermsAndConditions = () => {
	const { mode } = useTheme();

	const getBackgroundGradient = () => {
		return mode === 'dark'
			? 'linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%)'
			: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)';
	};

	const getPaperBgColor = () => {
		return mode === 'dark' ? '#18191b' : '#ffffff';
	};

	const getBorderColor = () => {
		return mode === 'dark' ? '#333' : '#e0e0e0';
	};

	const getBoxShadow = () => {
		return mode === 'dark'
			? '0 20px 40px rgba(0,0,0,0.3)'
			: '0 20px 40px rgba(0,0,0,0.08)';
	};

	const getTextColor = () => {
		return mode === 'dark' ? 'grey.300' : 'grey.700';
	};

	return (
		<Box sx={{
			minHeight: '100vh',
			background: getBackgroundGradient(),
			pt: 10,
			pb: 6
		}}>
			<Container maxWidth="lg">
				<motion.div
					initial={{ opacity: 0, y: 40 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7 }}
				>
					<Paper
						sx={{
							p: 6,
                            mt:3,
							borderRadius: 4,
							bgcolor: getPaperBgColor(),
							border: `1px solid ${getBorderColor()}`,
							boxShadow: getBoxShadow()
						}}
					>
						<Typography
							variant="h3"
							sx={{
								fontWeight: 700,
								color: 'secondary.main',
								mb: 4,
								textAlign: 'center'
							}}
						>
							Terms & Conditions
						</Typography>

						<Typography
							variant="body1"
							sx={{
								color: getTextColor(),
								mb: 4,
								lineHeight: 1.8,
								fontSize: '1.1rem'
							}}
						>
							By agreeing to these terms and conditions, the client confirms that he has understood and agrees on all the rates, notes and terms and conditions stated on this and attached page of this agreement and any other Annexure(s) uploaded to the account or in physical form.
						</Typography>

						<Box sx={{ mb: 4 }}>
							<Typography 
								variant="h5" 
								sx={{ 
									fontWeight: 600, 
									color: 'secondary.main', 
									mb: 2 
								}}
							>
								No Variation
							</Typography>
							<Typography
								variant="body1"
								sx={{
									color: getTextColor(),
									lineHeight: 1.8
								}}
							>
								These terms and conditions are applicable on all courier and logistics services provided by City COD (Private) Limited to the shipper/ Client hereunder. No employee, franchisee, agent of City COD (Private) Limited or anyone else has an authority to change any of these terms and conditions or make any promise on behalf of City COD (Private) Limited which is in addition to or conflict with these terms and conditions.
							</Typography>
						</Box>

						<Box sx={{ mb: 4 }}>
							<Typography 
								variant="h5" 
								sx={{ 
									fontWeight: 600, 
									color: 'secondary.main', 
									mb: 2 
								}}
							>
								Right of Inspection of Shipments
							</Typography>
							<Typography
								variant="body1"
								sx={{
									color: getTextColor(),
									lineHeight: 1.8
								}}
							>
								City COD (Private) Limited has the right but not the obligation to inspect any shipment including without limitation opening the shipment if required by law and/or regulations. In this regard the shipper/ client agrees that City COD (Private) Limited may open and inspect a shipment for any reason at any time.
							</Typography>
						</Box>

						<Box sx={{ mb: 4 }}>
							<Typography 
								variant="h5" 
								sx={{ 
									fontWeight: 600, 
									color: 'secondary.main', 
									mb: 2 
								}}
							>
								Lien of Goods Shipped
							</Typography>
							<Typography
								variant="body1"
								sx={{
									color: getTextColor(),
									lineHeight: 1.8
								}}
							>
								City COD (Private) Limited shall have lien on any goods shipped for all freight charges, Octroi, duties, advances or any other charges of any kind payable by the shipper/ client hereunder and may refuse to surrender possession of the goods until such charges are paid by the shipper/ client.
							</Typography>
						</Box>

						<Box sx={{ mb: 4 }}>
							<Typography 
								variant="h5" 
								sx={{ 
									fontWeight: 600, 
									color: 'secondary.main', 
									mb: 2 
								}}
							>
								Limitation of Liability
							</Typography>
							<Typography
								variant="body1"
								sx={{
									color: getTextColor(),
									lineHeight: 1.8
								}}
							>
								The liability of City COD (Private) Limited for any loss or damage to the shipment shall be limited to the declared value (along with reasonable evidence for determination of such value).
							</Typography>
						</Box>

						<Box sx={{ mb: 4 }}>
							<Typography 
								variant="h5" 
								sx={{ 
									fontWeight: 600, 
									color: 'secondary.main', 
									mb: 2 
								}}
							>
								Liabilities Not Assumed
							</Typography>
							<Typography
								variant="body1"
								sx={{
									color: getTextColor(),
									lineHeight: 1.8,
									mb: 2
								}}
							>
								The shipper/ sender acknowledges that the consignment/ shipment shall be transported by road and/or air and shall therefore be exposed to all risks associated with such mode of transportation. Subject to the aforesaid, City COD (Private) Limited will endeavor to exercise its best efforts to provide expeditious delivery in accordance with regular delivery schedule. However City COD (Private) Limited will not under any circumstances be liable for delay in pickup, transport or delivery of any shipment regardless of the cause of such delay. Further, City COD (Private) Limited shall not be liable if a shipment is lost, damaged, mis-delivered or not delivered because of circumstances beyond its control. These include:
							</Typography>
							<Box component="ul" sx={{ color: getTextColor(), pl: 3, lineHeight: 1.8 }}>
								<Box component="li" sx={{ mb: 1 }}>
									"Act of God" for example earthquake, cyclone, storm flood or "force majeure" for example war, plane crash or embargo or caused by mishap with City COD (Private) Limited.
								</Box>
								<Box component="li" sx={{ mb: 1 }}>
									The act, default or omission of the shipper, client or any other party who claims an interest in this shipment (include violation of any terms and condition hereof) or any person other than City COD (Private) Limited or of any government officials or of the postal service forwarded or other entity or person to whom a shipment is tendered by City COD (Private) Limited or transportation to any location not regularly served by City COD (Private) Limited regardless of whether the shipper/ client reported or had knowledge of such third party delivery arrangements.
								</Box>
								<Box component="li" sx={{ mb: 1 }}>
									The nature of the shipment for any defect characteristics inherent vice thereof even if known to City COD (Private) Limited when the shipper/ client accepted it.
								</Box>
								<Box component="li" sx={{ mb: 1 }}>
									Electrical/ magnetic energy erasure or other such damage to electronic or photographic images or recording in any form.
								</Box>
							</Box>
						</Box>

						<Box sx={{ mb: 4 }}>
							<Typography 
								variant="h5" 
								sx={{ 
									fontWeight: 600, 
									color: 'secondary.main', 
									mb: 2 
								}}
							>
								Claims
							</Typography>
							<Typography
								variant="body1"
								sx={{
									color: getTextColor(),
									lineHeight: 1.8,
									mb: 2
								}}
							>
								Any claim must be brought by the shipper/ client and delivered in writing to the office of City COD (Private) Limited nearest the location at which the shipment was accepted within 30 days of the date of such acceptance no claim may be made against City COD (Private) Limited outside of this time limit.
							</Typography>
							<Box component="ul" sx={{ color: getTextColor(), pl: 3, lineHeight: 1.8 }}>
								<Box component="li" sx={{ mb: 1 }}>
									No claim for loss or damage will be entertained until all transportation charges have been paid. The amount of any claim may not be deducted from any transportation charges owed to City COD (Private) Limited.
								</Box>
								<Box component="li" sx={{ mb: 1 }}>
									City COD (Private) Limited doesn't maintain records relevant to a shipment of more than 90 days. It will therefore be unable to refer to or produce any such record where it is notified for the same after expiry of the said period of 90 days from the date of shipment.
								</Box>
							</Box>
						</Box>

						<Box sx={{ mb: 4 }}>
							<Typography 
								variant="h5" 
								sx={{ 
									fontWeight: 600, 
									color: 'secondary.main', 
									mb: 2 
								}}
							>
								Lawfulness of Shipment/Goods
							</Typography>
							<Typography
								variant="body1"
								sx={{
									color: getTextColor(),
									lineHeight: 1.8
								}}
							>
								It is hereby undertaken and confirmed by shipper/client that the shipment or goods being stored/transported hereunder through City COD (Private) Limited have been acquired by the shipper/client lawfully and no law whatsoever has been breached in acquisition thereof. It is further undertaken and confirmed that all the relevant and necessary documents pertaining to the shipments/ goods are in possession of shipper/ client who will be liable and responsible to provide all and any document as and when required by City COD (Private) Limited for their onwards submission to any government agency department/ authority etc.
							</Typography>
						</Box>

						<Box sx={{ mb: 4 }}>
							<Typography 
								variant="h5" 
								sx={{ 
									fontWeight: 600, 
									color: 'secondary.main', 
									mb: 2 
								}}
							>
								Material Not Acceptable for Transportation
							</Typography>
							<Typography
								variant="body1"
								sx={{
									color: getTextColor(),
									lineHeight: 1.8
								}}
							>
								City COD (Private) Limited will not accept Cash, Prize bond/Defense saving certificates, Negotiable bonds, Antiques, Open/Bearer Cheques, Work of art, Bullion, Jewelry, Negotiable Instruments, Stamps, Precious metals/Stones, Liquor, Drug, Lose powder, Narcotics, Heroin, Dyes, Oily stuff, gums. Magnetic metal/ Materials, Liquid perishables, Grease, Glues, Battery cells, Fire arms & Explosives, Pornographic and smuggled goods and imported medicines from India, IATA restricted articles shipments/envelopes containing hate/ threatening material against President, Prime Minister, Governor, Ministers, MNA's, MPA's and High official of Government, Judiciary, Armed forces and Religion or sector against any individual any group organization and item the carriage of which is prohibited by any law, regulation or statute of any Provincial or Federal Government of Pakistan & mail or parcel on P.O box addresses. In compliance with post office Act 1898. City COD (Private) Limited will not accept letter or post cards as their carriage is the exclusive privilege of federal government.
							</Typography>
						</Box>

						<Box sx={{ mb: 4 }}>
							<Typography 
								variant="h5" 
								sx={{ 
									fontWeight: 600, 
									color: 'secondary.main', 
									mb: 2 
								}}
							>
								Taxes
							</Typography>
							<Typography
								variant="body1"
								sx={{
									color: getTextColor(),
									lineHeight: 1.8
								}}
							>
								GST and any other taxes imposed by local, provincial and federal Government of Pakistan will be applicable.
							</Typography>
						</Box>

						<Box sx={{ mb: 4 }}>
							<Typography 
								variant="h5" 
								sx={{ 
									fontWeight: 600, 
									color: 'secondary.main', 
									mb: 2 
								}}
							>
								Rates
							</Typography>
							<Typography
								variant="body1"
								sx={{
									color: getTextColor(),
									lineHeight: 1.8
								}}
							>
								Rates will be increased on yearly basis with mutual consent.
							</Typography>
						</Box>

						<Box sx={{ mb: 4 }}>
							<Typography 
								variant="h5" 
								sx={{ 
									fontWeight: 600, 
									color: 'secondary.main', 
									mb: 2 
								}}
							>
								Weight
							</Typography>
							<Typography
								variant="body1"
								sx={{
									color: getTextColor(),
									lineHeight: 1.8
								}}
							>
								Dense or volumetric weight will be charged whichever is higher.
							</Typography>
						</Box>
					</Paper>
				</motion.div>
			</Container>
		</Box>
	);
};

export default TermsAndConditions; 